// Dashboard hooks for the shifts surface. Read-side: GET /shifts/active
// (monitor/admin only). Write-side: POST /shifts/:id/force-end. The
// per-worker shift history endpoint exists on the server (GET /shifts/me
// for the worker themselves) but the dashboard doesn't surface it yet —
// when it does, we'll add a useUserShifts(userId) hook here.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface ShiftDto {
  id: string;
  organisation_id: string;
  user_id: string;
  vehicle: string | null;
  default_check_in_minutes: number;
  permissions_acknowledged: Record<string, unknown> | null;
  started_at: string;
  ended_at: string | null;
  ended_note: string | null;
  signature_svg_path: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ShiftSummaryDto {
  duration_seconds: number;
  check_ins_total: number;
  check_ins_completed: number;
  alarms_total: number;
  duress_alarms_total: number;
  distance_meters: number;
  journey_count: number;
}

export interface ShiftWithSummaryDto extends ShiftDto {
  summary: ShiftSummaryDto;
}

export interface ActiveShiftDto extends ShiftDto {
  user_display_name: string;
  user_email: string;
}

export function useActiveShifts() {
  const api = useApi();
  return useQuery({
    queryKey: ['shifts', 'active'],
    queryFn: () => api<{ shifts: ActiveShiftDto[] }>('/shifts/active'),
    // 60s backup poll. The WS hub doesn't yet publish shift.* events, so
    // this is the primary refresh path. Cheap enough at org scale (≤ a
    // few dozen active shifts at any time).
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useForceEndShift() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string | null }) =>
      api<ShiftWithSummaryDto>(`/shifts/${id}/force-end`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason ?? null }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shifts'] }),
  });
}
